import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { SummaryCardAction, SummaryCardHorizontalItem } from '@next/core-lib/components';
import { LocaleService } from '@next/core-lib/i18n';
import { DataSeries } from '@next/core-lib/models';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/shared/models';

import { Flock, FlockWeekData, WeekKPI, WeekRegistrationKPIItem } from '../../models/flock.model';
import { FlockWeekregistrationKpiService } from '../../services/flock-weekregistration-kpi.service';
import * as flockActions from '../../store/actions/flock.actions';

@Component({
  selector: 'app-flock-overview-week-registration-item',
  templateUrl: './flock-overview-week-registration-item.component.html',
  styleUrls: ['./flock-overview-week-registration-item.component.scss'],
  standalone: false,
})
export class FlockOverviewWeekRegistrationItemComponent implements OnInit {
  @Input() flock: Flock;
  @Input() flockWeekData: FlockWeekData;
  @Input() flockWeekDataSeries: DataSeries<number, number>;
  @Output() editClicked: EventEmitter<number> = new EventEmitter<number>();
  @Output() deleteClicked: EventEmitter<number> = new EventEmitter<number>();
  KPIs: WeekKPI;
  cardActions: SummaryCardAction[] = [];
  cardItems: SummaryCardHorizontalItem[] = [];

  constructor(
    private store: Store<AppState>,
    private flockWeekregistrationKpiService: FlockWeekregistrationKpiService,
    private localeService: LocaleService,
    private datePipe: DatePipe,
  ) {}

  ngOnInit(): void {
    this.cardItems = [];

    this.cardActions = [
      {
        id: 'edit-action',
        icon: 'edit',
        label: this.localeService.translate('flock-overview.week-registrations.edit'),
        callback: () => {
          this.editClicked.emit(this.flockWeekData.week);
        },
      },
      {
        id: 'delete-action',
        icon: 'delete',
        label: this.localeService.translate('flock-overview.week-registrations.delete'),
        callback: () => {
          this.deleteClicked.emit(this.flockWeekData.week);
        },
        disabled:
          this.flockWeekData.week !==
          this.flock.weekRegistrations.reduce((wr, u) => Math.max(wr, u.weekNr), 0),
      },
    ];

    this.KPIs = <WeekKPI>(
      this.flockWeekregistrationKpiService.getWeekKPI(this.flockWeekData, this.flockWeekDataSeries)
    );

    const exclude: string[] = ['week', 'weekStart'];
    const kpis = Object.keys(this.KPIs).filter((c) => !exclude.includes(c));
    kpis.forEach((kpi) => {
      const kpiItem = <WeekRegistrationKPIItem>(this.KPIs as { [index: string]: unknown })[kpi];

      // Show only KPI's we defined to show.
      if (kpiItem.showInOverview !== true) {
        return;
      }

      const cardItem: SummaryCardHorizontalItem = {
        label: this.localeService.translate(
          `flock-overview.week-registrations.kpis.${kpiItem.name}`,
        ),
        value: kpiItem.value,
        unit: kpiItem.unit,
        decimalPrecision: kpiItem.decimalsPrecision,
      };

      if (!this.isNaN(kpiItem.value)) {
        cardItem.valueAction = () => {
          this.showBenchmarkInfo(kpiItem);
        };
      }

      this.cardItems.push(cardItem);
    });
  }

  showBenchmarkInfo(kpiItem: WeekRegistrationKPIItem) {
    let useKPI: WeekRegistrationKPIItem;
    if (kpiItem.benchmark?.referenceKPI !== undefined) {
      useKPI = <WeekRegistrationKPIItem>(
        (this.KPIs as { [index: string]: WeekKPI })[kpiItem.benchmark.referenceKPI]
      );
    } else {
      useKPI = kpiItem;
    }
    this.store.dispatch(
      flockActions.loadFlockBenchmarks({
        flock: this.flock,
        kpi: useKPI,
      }),
    );
  }

  isNaN(value: number): boolean {
    return Number.isNaN(value) || !Number.isFinite(value);
  }

  getTitle(): string {
    return `<div>${this.localeService.translate('week-registration.week')} ${
      this.flockWeekData.week
    }</div>
    <div class="flex__child text--small">(${this.datePipe.transform(
      this.flockWeekData.weekStartDate,
    )} - ${this.datePipe.transform(this.flockWeekData.weekEndDate)})</div>`;
  }
}
